import React from 'react';
import { withRouter } from 'react-router-dom';
import { get, toNumber, flowRight as compose } from 'lodash';
import { add, format, getDay, parseISO, setDay } from 'date-fns';
import braintree from 'braintree-web';
import PropTypes from 'prop-types';

import isClient from 'commons/isClient';
import validateFormatZipCode from 'commons/validateFormatZipCode';
import withApolloClient from 'components/hoc/withApolloClient';

import { logInfo, logError } from 'commons/logger';
import {
  PLACE_ORDER,
  REMOVE_FROM_CART,
  ERASE_CHECKOUT_DATA,
  UPDATE_CART_PROTECTION_PLAN,
  UPDATE_CART_ITEM_PROTECTION_PLAN,
  LOG_BRAINTREE_CLIENT_NONCE_ERROR,
} from '@/graphql/mutations';
import {
  GET_CART,
  GET_FABRIC_DATA,
  GET_INSTORE_CHECKOUT_DATA,
  GET_CHECKOUT_SUMMARY,
  GET_CART_DELIVERY_DATES,
  GET_CART_SHIPPING_GROUPS,
  GET_BRAINTREE_CLIENT_TOKEN,
} from '@/graphql/queries';
import { getUserCurrentPageType } from 'hooks/useUserCurrentPageType';
import getZipcode from 'commons/getZipcode';
import getEnvValue from 'commons/getEnvValue';
import { getCheckoutRef } from './utils/checkout';
import {
  VALIDATE_ZIP_WITH_STATE,
  GET_CHECKOUT_DATA,
  GET_CART_SUMMARY,
} from './queries';
import avo from '../../../analytics/avo';

export const formatPhoneForEvent = phone =>
  phone ? phone.replace(/[^0-9]/g, '') : '';

// new Date('yyyy-MM-dd') is UTC midnight, the previous day in the US
export const toLocalDate = value =>
  typeof value === 'string' ? parseISO(value) : new Date(value);

export function getDelayedDeliveryStartDate(standardDeliveryEndDate) {
  if (!standardDeliveryEndDate) {
    return null;
  }
  const date = toLocalDate(standardDeliveryEndDate);
  // get nearest monday
  const startDate = setDay(date, getDay(date) <= 1 ? 1 : 8);
  // trunc time
  startDate.setHours(0, 0, 0, 0);
  return startDate;
}

export function getDelayedDeliveryTime(delayedDeliveryStartDate) {
  if (!delayedDeliveryStartDate) {
    return null;
  }
  const date = toLocalDate(delayedDeliveryStartDate);
  // get next friday
  return `${format(date, 'MMM d')} - ${format(
    add(date, { days: 4 }),
    'MMM d'
  )}`;
}

export const getBraintreeClientToken = async (checkoutData, client) => {
  const { shipping, email_address, orderSummary } = checkoutData;
  const { data: tokenResponse } = await client.query({
    query: GET_BRAINTREE_CLIENT_TOKEN,
    fetchPolicy: 'network-only',
    variables: {
      email: email_address,
      first_name: shipping.first_name,
      last_name: shipping.last_name,
      grand_total: orderSummary.grand_total,
      quick_checkout_guest: shipping.quick_checkout_guest ?? false,
    },
  });

  if (!tokenResponse.token || !tokenResponse.token.success) {
    return {
      error: get(
        tokenResponse,
        'token.message',
        'Failed to fetch payment client token'
      ),
    };
  }

  return { clientToken: tokenResponse.token.clientToken };
};

export const getPayPalData = async clientToken => {
  if (!isClient()) {
    return { status: 'false' };
  }

  let errors;
  let status = 'false';
  let paypalCheckoutInstance = null;

  try {
    await braintree.client
      .create({
        authorization: clientToken,
      })
      .then(clientInstance =>
        braintree.paypalCheckout.create({
          client: clientInstance,
        })
      )
      .then(_paypalCheckoutInstance =>
        _paypalCheckoutInstance.loadPayPalSDK({
          vault: true,
        })
      )
      .then(_paypalCheckoutInstance => {
        status = 'true';
        paypalCheckoutInstance = _paypalCheckoutInstance;
      })
      .catch(err => {
        logError(err);
        errors = [{ message: err.message }];
      });
  } catch (e) {
    logError(e, 'error from payPal config');
    errors = [{ message: e.message }];
  }

  return { status, errors, clientToken, paypalCheckoutInstance };
};

export const getApplePayData = async clientToken => {
  const APPLE_PAY_ENABLED =
    getEnvValue('NEXT_PUBLIC_APPLE_PAY_ENABLED') ??
    getEnvValue('REACT_APP_APPLE_PAY_ENABLED') ??
    '';
  if (
    !isClient() ||
    !window ||
    !window._env_ ||
    APPLE_PAY_ENABLED !== 'yes' ||
    !window.ApplePaySession ||
    !window.ApplePaySession.supportsVersion(3) ||
    !window.ApplePaySession.canMakePayments()
  ) {
    return { status: 'false' };
  }
  let errors;
  let status = 'false';
  let applePayInstance = null;
  try {
    // apple pay setup
    if (!window.ApplePaySession) {
      logError(new Error('This device does not support Apple Pay'));
    }

    if (!window.ApplePaySession.canMakePayments()) {
      logError(
        new Error('This device is not capable of making Apple Pay payments')
      );
    }

    logInfo('Apple pay supported');

    await braintree.client
      .create({
        authorization: clientToken,
      })
      .then(clientInstance =>
        braintree.applePay.create({
          client: clientInstance,
        })
      )
      .then(async _applePayInstance => {
        applePayInstance = _applePayInstance;

        await window.ApplePaySession.canMakePaymentsWithActiveCard(
          applePayInstance.merchantIdentifier
        ).then(canMakePaymentsWithActiveCard => {
          if (canMakePaymentsWithActiveCard) {
            status = 'true';
          }
        });
      })
      .catch(err => {
        logError(err);
        errors = [{ message: err.message }];
      });
  } catch (e) {
    logError(e, 'error from apple pay config');
    errors = [{ message: e.message }];
  }
  return { status, errors, clientToken, applePayInstance };
};

const withCheckoutUtils = WrappedComponent => {
  class WrappedComponentWithCheckoutUtils extends React.Component {
    getCart = async shippingZip => {
      const response = await this.props.client.query({
        query: GET_CART,
        fetchPolicy: 'network-only',
        context: { noBatch: true },
        variables: {
          shippingZip,
          zipCode: getZipcode(),
          checkoutRef: getCheckoutRef(),
        },
      });
      return response.data.getCart;
    };

    getCartSummary = async () => {
      const response = await this.props.client.query({
        query: GET_CART_SUMMARY,
        fetchPolicy: 'network-only',
        context: { noBatch: true },
      });
      return response.data.cartSummary;
    };

    getCheckoutSummary = async () => {
      const checkoutRef = getCheckoutRef();
      const response = await this.props.client.query({
        query: GET_CHECKOUT_SUMMARY,
        fetchPolicy: 'network-only',
        context: { noBatch: true },
        variables: {
          ...(checkoutRef ? { checkoutRef } : {}),
          input: {
            zipcode: getZipcode(),
          },
        },
      });
      return response.data.checkoutSummary;
    };

    getFabricsData = async () => {
      const response = await this.props.client.query({
        query: GET_FABRIC_DATA,
        variables: {},
      });
      return response.data.getFabrics;
    };

    getShippingGroups = async (
      shipping = {},
      deliveryInfo = {},
      fetchPolicy = 'network-only'
    ) => {
      try {
        const response = await this.props.client.query({
          query: GET_CART_SHIPPING_GROUPS,
          fetchPolicy,
          variables: {
            input: {
              state: shipping.state,
              zipcode: validateFormatZipCode(shipping.zipcode)
                ? shipping.zipcode
                : '',
              delivery_selection: deliveryInfo.delivery_selection,
              production_selection: deliveryInfo.production_selection,
              production_selection_add: deliveryInfo.production_selection_add,
            },
            visitorZip: getZipcode(),
            ...(getCheckoutRef() ? { checkoutRef: getCheckoutRef() } : {}),
          },
        });
        return response.data.getShippingGroups;
      } catch (e) {
        return { errors: [{ message: e.message }] };
      }
    };

    getDeliveryDates = async (shipping = {}, deliveryInfo = {}) => {
      try {
        const response = await this.props.client.query({
          query: GET_CART_DELIVERY_DATES,
          fetchPolicy: 'network-only',
          variables: {
            input: {
              state: shipping.state,
              zipcode: shipping.zipcode,
              delivery_selection: deliveryInfo.delivery_selection,
              production_selection: deliveryInfo.production_selection,
              production_selection_add: deliveryInfo.production_selection_add,
            },
            visitorZip: getZipcode(),
          },
        });
        return response.data.getDeliveryDates;
      } catch (e) {
        return { errors: [{ message: e.message }] };
      }
    };

    getBraintreeNonce = async (
      {
        email_address,
        shipping,
        payment,
        grandTotal,
        clientAuth: currentClientAuth,
      },
      cardIndex = 0
    ) => {
      let clientAuth = currentClientAuth ? { ...currentClientAuth } : null;

      if (!clientAuth) {
        const { clientToken } = await getBraintreeClientToken(
          {
            email_address,
            shipping,
            orderSummary: { grand_total: grandTotal },
          },
          this.props.client
        );

        clientAuth = {
          email_address,
          first_name: shipping.first_name,
          last_name: shipping.last_name,
          token: clientToken,
        };
      }

      if (!clientAuth?.token) {
        throw new Error(
          'Unable to place your order. Please try again after sometime or contact the customer support.'
        );
      }

      const { error, nonce } = await new Promise(resolve => {
        braintree.client
          .create({
            authorization: clientAuth.token,
          })
          .then(clientInstance => {
            const {
              number,
              cvc,
              expiry,
              isBillingAddressSame,
              billingAddress,
            } = payment.credit_cards[cardIndex];
            const data = {
              creditCard: {
                cardholderName: `${shipping.first_name} ${shipping.last_name}`,
                number,
                cvv: cvc,
                expirationDate: expiry,
                billingAddress: {
                  postalCode: isBillingAddressSame
                    ? shipping.zipcode
                    : billingAddress.zipcode,
                  // streetAddress: isBillingAddressSame ? shipping.address : billingAddress.address
                },
                options: {
                  validate: true,
                },
              },
            };

            // Warning: For a merchant to be eligible for the easiest level of PCI compliance (SAQ A),
            // payment fields cannot be hosted on your checkout page.
            // For an alternative to the following, use Hosted Fields.
            clientInstance.request(
              {
                endpoint: 'payment_methods/credit_cards',
                method: 'post',
                data,
              },
              (requestErr, response) => {
                if (requestErr) {
                  let err = requestErr;
                  if (requestErr.code === 'CLIENT_REQUEST_ERROR') {
                    err = new Error(
                      'Please check your credit card details or billing address and try again'
                    );
                  }

                  // https://stitchindustries.atlassian.net/browse/CON-5818: log error to alexandria
                  this.props.client
                    .mutate({
                      mutation: LOG_BRAINTREE_CLIENT_NONCE_ERROR,
                      variables: {
                        email: email_address,
                        grand_total: grandTotal,
                      },
                    })
                    .catch(console.error); // eslint-disable-line no-console

                  const originalErrorMessage =
                    requestErr?.details?.originalError?.error?.message ||
                    'Error in getting nonce from braintree';

                  logError(requestErr, originalErrorMessage, {
                    email_address,
                    shipping,
                    isBillingAddressSame,
                    billingAddress,
                    type: 'nonce_creation',
                    requestErr: {
                      ...requestErr,
                      details: {
                        ...requestErr.details,
                        originalError: JSON.stringify(
                          requestErr.details?.originalError
                        ),
                      },
                    },
                  });
                  return resolve({ error: err });
                }

                return resolve({ nonce: response.creditCards[0].nonce });
              }
            );
          })
          .catch(e => {
            resolve({ error: e });
          });
      });

      if (error) {
        throw new Error(error);
      }

      return nonce;
    };

    rehydrateCheckouts = async () => {
      try {
        const checkout_ref = getCheckoutRef();
        const res = await this.props.client.query({
          query: checkout_ref ? GET_INSTORE_CHECKOUT_DATA : GET_CHECKOUT_DATA,
          ...(checkout_ref
            ? {
                variables: {
                  checkoutRef: checkout_ref,
                },
              }
            : {}),
          fetchPolicy: 'network-only',
        });

        const data = get(res, 'data.getCheckoutData', {});
        const newState = {};

        if (data) {
          if (data.email_address) newState.email_address = data.email_address;
          if (data.selectedStoreEmployee)
            newState.selectedStoreEmployee = data.selectedStoreEmployee;
          if (data.contact_phone_number !== undefined)
            newState.contact_phone_number = data.contact_phone_number;
          if (data.sms_marketing_opt_in !== undefined)
            newState.sms_marketing_opt_in = data.sms_marketing_opt_in;
          if (data.shipping) newState.shipping = data.shipping;
          if (data.deliveryInfo) newState.deliveryInfo = data.deliveryInfo;
          if (data.selected_tab) newState.selected_tab = data.selected_tab;
          if (data.tab_progress >= 0) newState.tab_progress = data.tab_progress;
        }

        return newState;
      } catch (e) {
        return { errors: [{ message: e.message }] };
      }
    };

    removeFromCart = async item => {
      try {
        return await this.props.client.mutate({
          mutation: REMOVE_FROM_CART,
          variables: {
            id: item.id,
          },
        });
      } catch (e) {
        return { errors: [{ message: e.message }] };
      }
    };

    calculateTotal = async ({ email_address, shipping, deliveryInfo }) => {
      try {
        const { shipping_sms, phone_number, state, zipcode, address, city } =
          shipping || {};
        const {
          delivery_selection,
          production_selection,
          production_selection_add,
        } = deliveryInfo || {};
        const checkoutSummary = await this.props.client.query({
          query: GET_CHECKOUT_SUMMARY,
          variables: {
            checkoutRef: getCheckoutRef(),
            input: {
              delivery_selection,
              production_selection,
              production_selection_add,
              customer_email: email_address,
              shipping_sms: {
                status: shipping_sms ? 1 : 0,
                phone: shipping_sms ? phone_number : null,
              },
              state,
              zipcode,
              address,
              city,
            },
          },
          fetchPolicy: 'network-only',
        });
        return get(checkoutSummary, 'data.checkoutSummary', {});
      } catch (e) {
        return { errors: [{ message: e.message }] };
      }
    };

    placeOrder = async ({
      cart,
      email_address,
      retail_id,
      entered_by,
      shipping,
      payment,
      deliveryInfo,
      orderSummary,
      clientAuth,
    }) => {
      try {
        let order = {
          cart,
          retail_id,
          entered_by,
          shipping: {
            ...shipping,
            email_address,
          },
          payment,
          deliveryInfo,
          orderSummary,
          visitorZip: getZipcode(),
        };

        switch (payment.selected_type) {
          case 'credit_card': {
            const promises = [];
            promises.push(
              this.getBraintreeNonce({
                email_address,
                shipping,
                payment,
                clientAuth,
                grandTotal: orderSummary.grand_total,
              })
            );
            if (payment.credit_cards.length === 2) {
              promises.push(
                this.getBraintreeNonce(
                  {
                    email_address,
                    shipping,
                    payment,
                    clientAuth,
                    grandTotal: orderSummary.grand_total,
                  },
                  1
                )
              );
            }
            const [braintreeNonce, secondBraintreeNonce] = await Promise.all(
              promises
            );
            order = {
              ...order,
              payment: {
                ...order.payment,
                credit_cards: payment.credit_cards.map(c => ({
                  ...c,
                  number: 'n/a',
                  expiry: 'n/a',
                  cvc: 'n/a',
                })),
                braintree_nonce: braintreeNonce,
                second_braintree_nonce: secondBraintreeNonce,
              },
            };
            break;
          }
          case 'paypal': {
            order = {
              ...order,
              payment: {
                ...order.payment,
                braintree_nonce: payment.braintree_nonce,
              },
            };
            break;
          }
          case 'apple_pay': {
            order = {
              ...order,
              payment: {
                ...order.payment,
                braintree_token: payment.braintree_token,
              },
            };
            break;
          }
          default:
            break;
        }
        const orderResult = await this.props.client.mutate({
          mutation: PLACE_ORDER,
          variables: {
            order: JSON.stringify(order),
            checkoutRef: getCheckoutRef(),
            quick_checkout_guest: false,
            consumerVersion:
              getEnvValue('REACT_APP_PACKAGE_VERSION') ||
              getEnvValue('NEXT_PUBLIC_PACKAGE_VERSION'),
          },
        });

        logInfo('order result api call', orderResult);

        const response = get(orderResult, 'data.placeOrder', {});

        // erase checkout only if order is successful
        if (response.orderId) {
          await Promise.all([
            this.getCart(),
            this.props.client.mutate({
              mutation: ERASE_CHECKOUT_DATA,
              variables: {
                checkoutRef: getCheckoutRef(),
              },
            }),
          ]);
        }

        return response;
      } catch (e) {
        return { errors: [{ message: e.message }] };
      }
    };

    toggleCartProtectionPlan = async isEnabled => {
      try {
        const response = await this.props.client.mutate({
          mutation: UPDATE_CART_PROTECTION_PLAN,
          variables: {
            protection_plan: isEnabled,
          },
        });

        return get(response, 'data.updateCartProtectionPlan', {});
      } catch (e) {
        return { errors: [{ message: e.message }] };
      }
    };

    toggleCartItemProtectionPlan = async (cart_id, plan_id, isEnabled) => {
      try {
        const response = await this.props.client.mutate({
          mutation: UPDATE_CART_ITEM_PROTECTION_PLAN,
          variables: {
            cart_id: toNumber(cart_id),
            plan_id: toNumber(plan_id),
            protection_plan: isEnabled,
          },
        });
        const [cart] = await Promise.all([
          this.getCart(),
          this.getCheckoutSummary(),
        ]);
        const cartItem = cart?.cart.filter(item => item.id === cart_id)[0];

        const fabric = cartItem.options.filter(
          option => option.optionTypeName === 'Fabric'
        )[0];
        const optionPrice =
          cartItem.options.reduce(
            (price, item) => price + item.additional_cost,
            0
          ) || 0;
        const selectedPlan = cartItem?.eligible_protection_plans?.filter(
          plan => plan.plan_id === plan_id
        )[0];
        const location = getUserCurrentPageType(this.props?.match);
        avo.protectionPlanAdded({
          fabricColor: fabric?.name || '',
          fabricId: fabric?.id || 0,
          fabricName: fabric?.name || '',
          favorited: cartItem.product?.meta?.customer?.favorited || false,
          fullPrice: parseFloat(cartItem.price_pre_discount + optionPrice),
          planPrice: parseFloat(selectedPlan?.plan_price || 0),
          planName:
            selectedPlan?.plan_name === 'Pet Protector Plan'
              ? 'Pet Protector'
              : 'Life Happens',
          price: parseFloat(cartItem.price + optionPrice),
          productFamily: cartItem.product?.family?.name || '',
          productId: cartItem.product_id,
          location,
          name: cartItem.name,
          category: cartItem.product?.type.name,
          component: location === 'Checkout' ? null : avo.Component.CART,
        });
        return get(response, 'data.updateCartItemProtectionPlan', {});
      } catch (e) {
        return { errors: [{ message: e.message }] };
      }
    };

    validateZipWithState = async (zip, state) => {
      try {
        const response = await this.props.client.query({
          query: VALIDATE_ZIP_WITH_STATE,
          variables: { zip, state },
          fetchPolicy: 'network-only',
        });
        return response.data && response.data.validateZipWithState;
      } catch (e) {
        return { isValid: false, errors: [{ message: e.message }] };
      }
    };

    render() {
      const checkoutUtilActions = {
        getCheckoutRef,
        getCart: this.getCart,
        getFabricsData: this.getFabricsData,
        getCartSummary: this.getCartSummary,
        getCheckoutSummary: this.getCheckoutSummary,
        getShippingGroups: this.getShippingGroups,
        getDeliveryDates: this.getDeliveryDates,
        updateDelayedShippingDates: this.updateDelayedShippingDates,
        rehydrateCheckouts: this.rehydrateCheckouts,
        calculateTotal: this.calculateTotal,
        placeOrder: this.placeOrder,
        removeFromCart: this.removeFromCart,
        toggleCartProtectionPlan: this.toggleCartProtectionPlan,
        toggleCartItemProtectionPlan: this.toggleCartItemProtectionPlan,
        validateZipWithState: this.validateZipWithState,
        getBraintreeNonce: this.getBraintreeNonce,
      };

      return (
        <WrappedComponent
          {...this.props}
          client={this.props.client}
          checkoutUtilActions={checkoutUtilActions}
        />
      );
    }
  }

  WrappedComponentWithCheckoutUtils.propTypes = {
    client: PropTypes.shape({
      query: PropTypes.func.isRequired,
      mutate: PropTypes.func.isRequired,
    }).isRequired,
    match: PropTypes.object,
  };

  WrappedComponentWithCheckoutUtils.displayName = `${WrappedComponent.displayName ||
    WrappedComponent.name}WithCheckoutUtils`;

  return compose(
    withApolloClient,
    withRouter
  )(WrappedComponentWithCheckoutUtils);
};

export default withCheckoutUtils;
